// TODO - JSDOC format, look into subfields
import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { CSVLink } from "react-csv";
import { sanitizeRecord, makeAdjustedString, safeJSONParse } from "../../util";
import JSON5 from 'json5';
import { Paragraph, toaster } from 'evergreen-ui';
import { QUERY_PRODUCTS } from '../../graphql/queries/Products/QueryProducts';
import QUERY_ORDERS from "../../graphql/queries/Orders/QueryOrders";
import QUERY_CREATORS from "../../graphql/queries/Creators/QueryCreators";
import QUERY_CUSTOMERS from "../../graphql/queries/Customers/QueryCustomers";
import QUERY_EXPENSES from "../../graphql/queries/Expenses/QueryExpenses";
import QUERY_ROYALTYS from "../../graphql/queries/Royaltys/QueryRoyaltys";
import QUERY_ROYALTY_PAYMENTS from "../../graphql/queries/Royaltys/QueryRoyaltyPayments";
import CsvDownloadButton from 'react-json-to-csv'

/**
 * CsvDownload Component
 * Handles CSV exports for various data types in the application
 */
function CsvDownload(props) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const [fetched, setFetched] = useState([]);
  const [exportAttempted, setExportAttempted] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Generate a unique filename based on current timestamp
  const timestamp = new Date(Date.now());
  const filename = `${props.csvType}-${timestamp.getFullYear()}-${timestamp.getMonth() + 1}-${timestamp.getDate()}-${timestamp.getHours()}${timestamp.getMinutes()}${timestamp.getSeconds()}.csv`;

  // =============== DATA PROCESSING UTILITIES ===============

  /**
   * Extract values from a nested object based on specified fields
   */
  const extractSetItemValues = (setItem, fields, subfields = null) => {
    try {
      if (!setItem || !fields) {
        return '';
      }

      const values = fields.map(field => {
        if (subfields && subfields[field]) {
          // Handle subfield logic here if needed
          return '';
        } else {
          return setItem[field] || '';
        }
      }).filter(Boolean);

      return values.join(" -- ");
    } catch (error) {
      console.error('Error in extractSetItemValues:', error);
      return '';
    }
  };

  const formatCountryData = (countryData) => {
    // If country is already a string but looks like an object, try to parse it
    if (typeof countryData === 'string' && countryData.includes('{')) {
      try {
        const parsedCountry = JSON.parse(countryData.replace(/'/g, '"'));
        if (parsedCountry && parsedCountry.name) {
          return parsedCountry.name;
        }
      } catch (e) {
        // If parsing fails, return as is
      }
    }
    // If country is an object with name property
    else if (typeof countryData === 'object' && countryData && countryData.name) {
      return countryData.name;
    }

    // Default: return as is
    return countryData;
  };

  // =============== DATA TYPE PROCESSORS ===============

  // Processor registry - Each export type has its own data processor
  const dataProcessors = {
    /**
     * Process Products data for CSV export
     */
    Products: (items, config = {}) => {
      // --- DEBUG LOG ---
      // console.log('[Products Processor] Received items:', items);
      // console.log('[Products Processor] Number of items:', items?.length);
      // --- END DEBUG LOG ---

      // Revised helper function to parse measurement fields (value + unit) more robustly
      const formatMeasurement = (fieldData) => {
        if (!fieldData) return '';

        let value = '';
        let unit = '';
        let data = fieldData;

        // Attempt to parse if it's a string that looks like an object
        if (typeof fieldData === 'string' && fieldData.trim().startsWith('{') && fieldData.trim().endsWith('}')) {
            try {
                 // Try JSON5 parsing first for better tolerance
                const parsed = JSON5.parse(fieldData);
                if (parsed && typeof parsed === 'object' && parsed !== null) {
                    data = parsed; // Use the parsed object
                }
                // If parsing fails, we might still have value/unit within the string
            } catch(e) {
                 // Ignore parsing errors, attempt regex extraction below
                 data = fieldData; // Keep as string for regex
            }
        }

        // If we have an object now (parsed or original)
        if (typeof data === 'object' && data !== null) {
            value = data.value || '';
            unit = data.unit || '';
        }
        // If it's still a string (original or failed parse)
        else if (typeof data === 'string') {
            // Try to extract value and unit using regex, accommodating variations
            const valueMatch = data.match(/['"]?value['"]?\s*:\s*['"]?([^'"}\s,]+)['"]?/);
            const unitMatch = data.match(/['"]?unit['"]?\s*:\s*['"]?([^'"}\s,]+)['"]?/);

            if (valueMatch && valueMatch[1]) {
                value = valueMatch[1];
            }
            if (unitMatch && unitMatch[1]) {
                unit = unitMatch[1];
            }

            // If regex fails and it's just a simple string/number, use it as the value
            if (!value && !unit && !data.includes(':') && !data.includes('{')) {
               value = data.trim();
            }
        }
         // Handle if data is a simple number originally
        else if (typeof data === 'number') {
            value = String(data);
        }


        // Clean up extracted values
        value = String(value).trim();
        unit = String(unit).trim();

        // Return combined string or just value, or empty string if no value
        if (value && unit) {
          return `${value} ${unit}`;
        } else if (value) {
          return value;
        } else {
          return ''; // Explicitly return empty string if no value found
        }
      };

      return items.map(item => {
        // --- DEBUGGING LOGS ---
        // console.log('--- Product Item Start ---');
        // console.log('Item ID:', item?.id);
        // console.log('Raw item.format:', item?.format);
        // console.log('Raw item.productcreatorSet:', item?.productcreatorSet);
        // console.log('--- Product Item End ---');
        // --- END DEBUGGING LOGS ---
        try {
          // Initialize newItem with all expected fields set to empty strings or default values
          const newItem = {
            id: '',
            isbn: '',
            title: '',
            subtitle: '',
            format_description: '',
            creators: '',
            description: '',
            publication_date: '',
            status_description: '',
            retail_price: '',
            weight_value: '',
            width_value: '',
            height_value: '',
            thickness_value: '',
            page_count: '',
            edition: '',
            volume: '',
            series: '',
            imprint_value: '',
            publisher_name: '',
            ship_date: '',
            carton_qty: '',
            inventory_quantity: '',
            bisac: '',
            audience_description: '',
            audience_details: '',
            admin_notes: '',
          };

          // Map basic fields
          newItem.id = item.id || '';
          newItem.isbn = item.isbn || '';

          // Use ProductWork fields if available, otherwise fallback to Product format fields
          newItem.title = item.productWork?.title || item.title || '';
          newItem.subtitle = item.subtitle || ''; // Subtitle likely stays with the format
          newItem.description = item.productWork?.description || item.description || ''; // Assuming description is primarily from ProductWork now

          newItem.publication_date = item.publicationDate || '';
          newItem.retail_price = item.retailPrice || '';
          newItem.edition = item.edition || '';
          newItem.volume = item.volume || '';
          newItem.ship_date = item.shipDate || '';
          newItem.carton_qty = item.cartonQty || '';
          newItem.inventory_quantity = item.inventoryQuantity || '';

          // Map BISAC using simpler Regex to extract only alphanumeric codes
          newItem.bisac = ''; // Initialize
          if (typeof item.bisac === 'string') {
            // Regex to find sequences of uppercase letters and numbers
            const regex = /[A-Z0-9]+/g;
            const bisacCodes = item.bisac.match(regex);

            // If codes were found (match returns an array or null)
            if (bisacCodes && bisacCodes.length > 0) {
              newItem.bisac = bisacCodes.join('; ');
            } else {
              // Fallback: If no alphanumeric codes found, keep it empty or use original non-parsed string?
              // Let's default to empty if specific format isn't matched.
              newItem.bisac = '';
            }
          } else {
            // If it's not a string, assign original value or empty
            newItem.bisac = item.bisac || ''; // Handles null/undefined
          }

          newItem.audience_details = item.audienceDetails || '';
          newItem.admin_notes = item.adminNotes || '';

          // Map nested/complex fields more robustly
          // Format - Access description directly from the object
          newItem.format_description = item.format?.description || '';

          // Creators - Parse string array elements, then nested creator string
          if (item.productcreatorSet && Array.isArray(item.productcreatorSet)) {
            const creatorNames = item.productcreatorSet.map(pcString => {
              // Access displayName directly from nested creator object
              return pcString.creator?.displayName || null;
            });
            newItem.creators = creatorNames.filter(Boolean).join('; ');
          } else {
            newItem.creators = ''; // Ensure it's blank if missing or not an array
          }

          // Access nested fields directly
          newItem.status_description = item.status?.description || '';
          newItem.series = item.series?.value || '';
          newItem.imprint_value = item.productWork?.imprint?.value || item.imprint?.value || '';
          newItem.publisher_name = item.productWork?.publisher?.name || item.publisher?.name || ''; // Publisher comes from ProductWork
          newItem.audience_description = item.audience?.description || '';

          // Use the helper for measurement fields
          newItem.weight_value = formatMeasurement(item.weight);
          newItem.width_value = formatMeasurement(item.width);
          newItem.height_value = formatMeasurement(item.height);
          newItem.thickness_value = formatMeasurement(item.thickness);
          newItem.page_count = formatMeasurement(item.pageCount); // Ensure source field 'pageCount' is used

          // No longer need generic processing for Products
          // processConfigFields(newItem, item, config);

          return newItem;
        } catch (error) {
          console.error('Error processing Product:', error);
          // Return a minimal item in case of error
          return { id: item?.id || '', title: item?.title || '' };
        }
      });
    },

    /**
     * Process Orders data for CSV export
     */
    Orders: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item }; // Start with sanitized item fields

          // Initialize fields to ensure they exist
          newItem.company = '';
          newItem.order_address_line1 = '';
          newItem.order_address_line2 = '';
          newItem.order_address_city = '';
          newItem.order_address_stateOrRegion = '';
          newItem.order_address_zipcode = '';
          newItem.order_address_country = '';
          newItem.order_email_value = '';
          newItem.order_phone_value = '';
          newItem.payment_term_value = '';
          newItem.salesperson_value = '';
          newItem.shipping_type_value = '';

          // Basic order fields (overwriting if they exist in item)
          newItem.id = item.id || '';
          newItem.po_number = item.poNumber || '';
          newItem.total_amount = item.totalAmount || '';
          newItem.shipping_cost = item.shippingCost || '';
          newItem.paid = item.paid || false;
          newItem.paid_date = item.paidDate || '';
          newItem.shipped = item.shipped || false;
          newItem.ship_date = item.shipDate || '';
          newItem.admin_notes = item.adminNotes || '';

          // Process customer company name
          let customerData = null;
          if (item.customer) {
            customerData = typeof item.customer === 'string'
              ? safeJSONParse(item.customer)
              : item.customer;
            newItem.company = customerData?.company || '';
          }

          // --- Address Logic ---
          let addressFound = false;
          if (item.orderAddress) {
            try {
              let addressData = typeof item.orderAddress === 'string'
                ? safeJSONParse(item.orderAddress)
                : item.orderAddress;

              if (addressData && addressData.line1) { // Check if addressData is valid and has line1
                newItem.order_address_line1 = addressData.line1 || '';
                newItem.order_address_line2 = addressData.line2 || '';
                newItem.order_address_city = addressData.city || '';
                newItem.order_address_stateOrRegion = addressData.stateOrRegion || '';
                newItem.order_address_zipcode = addressData.zipcode || '';
                newItem.order_address_country = formatCountryData(addressData.country) || '';
                addressFound = true;
              }
            } catch (error) {
              console.error('Error processing direct order address:', error);
            }
          }

          // Fallback to customer's primary address if not found on order
          if (!addressFound && customerData?.customeraddressSet && Array.isArray(customerData.customeraddressSet)) {
            const primaryAddress = customerData.customeraddressSet.find(addr => addr.primary === true) || customerData.customeraddressSet[0];
            if (primaryAddress) {
              newItem.order_address_line1 = primaryAddress.line1 || '';
              newItem.order_address_line2 = primaryAddress.line2 || '';
              newItem.order_address_city = primaryAddress.city || '';
              newItem.order_address_stateOrRegion = primaryAddress.stateOrRegion || '';
              newItem.order_address_zipcode = primaryAddress.zipcode || '';
              newItem.order_address_country = formatCountryData(primaryAddress.country) || '';
            }
          }

          // --- Email Logic ---
          let emailFound = false;
          if (item.orderEmail) {
            try {
              let emailData = typeof item.orderEmail === 'string'
                ? safeJSONParse(item.orderEmail)
                : item.orderEmail;

              if (emailData && emailData.value) { // Check if emailData is valid and has value
                newItem.order_email_value = emailData.value || '';
                emailFound = true;
              }
            } catch (error) {
              console.error('Error processing direct order email:', error);
            }
          }

          // Fallback to customer's primary email if not found on order
          if (!emailFound && customerData?.customeremailSet && Array.isArray(customerData.customeremailSet)) {
            const primaryEmail = customerData.customeremailSet.find(email => email.primary === true) || customerData.customeremailSet[0];
            if (primaryEmail) {
              newItem.order_email_value = primaryEmail.value || '';
            }
          }

          // --- Phone Logic ---
          let phoneFound = false;
          if (item.orderPhone) {
            try {
              let phoneData = typeof item.orderPhone === 'string'
                ? safeJSONParse(item.orderPhone)
                : item.orderPhone;

              if (phoneData && phoneData.value) { // Check if phoneData is valid and has value
                newItem.order_phone_value = phoneData.value || '';
                phoneFound = true;
              }
            } catch (error) {
              console.error('Error processing direct order phone:', error);
            }
          }

          // Fallback to customer's primary phone if not found on order
          if (!phoneFound && customerData?.customerphonenumberSet && Array.isArray(customerData.customerphonenumberSet)) {
            const primaryPhone = customerData.customerphonenumberSet.find(phone => phone.primary === true) || customerData.customerphonenumberSet[0];
            if (primaryPhone) {
              newItem.order_phone_value = primaryPhone.value || '';
            }
          }

          // Process payment term if it exists
          if (item.paymentTerm) {
            try {
              let termData = typeof item.paymentTerm === 'string'
                ? safeJSONParse(item.paymentTerm)
                : item.paymentTerm;
              newItem.payment_term_value = termData?.value || '';
            } catch (error) {
              console.error('Error processing payment term:', error);
            }
          }

          // Process salesperson if it exists
          if (item.salesperson) {
            try {
              let salesData = typeof item.salesperson === 'string'
                ? safeJSONParse(item.salesperson)
                : item.salesperson;
              newItem.salesperson_value = salesData?.value || '';
            } catch (error) {
              console.error('Error processing salesperson:', error);
            }
          }

          // Process shipping type if it exists
          if (item.shippingType) {
            try {
              let shippingData = typeof item.shippingType === 'string'
                ? safeJSONParse(item.shippingType)
                : item.shippingType;
              newItem.shipping_type_value = shippingData?.value || '';
            } catch (error) {
              console.error('Error processing shipping type:', error);
            }
          }

          // Remove original complex fields that have been processed
          delete newItem.customer;
          delete newItem.orderAddress;
          delete newItem.orderEmail;
          delete newItem.orderPhone;
          delete newItem.paymentTerm;
          delete newItem.salesperson;
          delete newItem.shippingType;
          delete newItem.customeraddressSet; // Remove potential duplicates if sanitized badly
          delete newItem.customeremailSet;
          delete newItem.customerphonenumberSet;

          // Process remaining fields based on config (less likely needed now)
          // processConfigFields(newItem, item, config); // Consider removing if specific processing covers all headers

          return newItem;
        } catch (error) {
          console.error('Error processing Order:', error);
          // Return at least some data even in case of error
          return {
            id: item.id || '',
            paid: item.paid || false,
            shipped: item.shipped || false,
            total_amount: item.totalAmount || ''
          };
        }
      });
    },

    /**
     * Process Customers data for CSV export
     */
    Customers: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item }; // Start with sanitized item data

          // --- Initialize all expected output fields ---
          newItem.first_name = '';
          newItem.last_name = '';
          newItem.company = '';
          newItem.addresses_line1 = '';
          newItem.addresses_line2 = '';
          newItem.addresses_city = '';
          newItem.addresses_stateOrRegion = '';
          newItem.addresses_zipcode = '';
          newItem.addresses_country = '';
          newItem.emails_value = '';
          newItem.phoneNumbers_value = '';
          newItem.payment_terms_value = '';
          newItem.customer_class_value = '';
          newItem.out_of_business = false;
          newItem.admin_notes = '';

          // --- Process basic fields ---
          newItem.first_name = item.firstName || '';
          newItem.last_name = item.lastName || '';
          newItem.company = item.company || '';
          newItem.out_of_business = item.outOfBusiness || false;
          newItem.admin_notes = item.adminNotes || '';

          // --- Process Addresses ---
          if (item.customeraddressSet && Array.isArray(item.customeraddressSet) && item.customeraddressSet.length > 0) {
            let targetAddressString = null;
            // Find the string containing the primary address
            const primaryAddressString = item.customeraddressSet.find(addrString => {
              const addrObj = safeJSONParse(addrString); // Parse the string
              return addrObj?.primary == true; // Check primary on the parsed object
            });

            if (primaryAddressString) {
              targetAddressString = primaryAddressString;
            } else {
              // Fallback to the first string in the array if no primary found
              targetAddressString = item.customeraddressSet[0];
            }

            // If we have a string (either primary or fallback)
            if (targetAddressString) {
              const parsedAddress = safeJSONParse(targetAddressString); // Parse the selected string
              if(parsedAddress) { // Check if parsing was successful
                 newItem.addresses_line1 = parsedAddress.line1 || '';
                 newItem.addresses_line2 = parsedAddress.line2 || '';
                 newItem.addresses_city = parsedAddress.city || '';
                 newItem.addresses_stateOrRegion = parsedAddress.stateOrRegion || '';
                 newItem.addresses_country = formatCountryData(parsedAddress.country) || '';
                 newItem.addresses_zipcode = parsedAddress.zipcode || '';
              }
            }
          }

          // --- Process Emails ---
          if (item.customeremailSet && Array.isArray(item.customeremailSet) && item.customeremailSet.length > 0) {
             let targetEmailString = null;
             // Find the string containing the primary email
            const primaryEmailString = item.customeremailSet.find(emailString => {
              const emailObj = safeJSONParse(emailString);
              return emailObj?.primary == true;
            });

            if (primaryEmailString) {
              targetEmailString = primaryEmailString;
            } else {
               targetEmailString = item.customeremailSet[0];
            }

            // If we have a string
            if (targetEmailString) {
              const parsedEmail = safeJSONParse(targetEmailString); // Parse the selected string
               if(parsedEmail) {
                  newItem.emails_value = parsedEmail.value || '';
               }
            }
          }

          // --- Process Phone Numbers ---
          if (item.customerphonenumberSet && Array.isArray(item.customerphonenumberSet) && item.customerphonenumberSet.length > 0) {
            let targetPhoneString = null;
             // Find the string containing the primary phone
            const primaryPhoneString = item.customerphonenumberSet.find(phoneString => {
              const phoneObj = safeJSONParse(phoneString);
              return phoneObj?.primary == true;
            });

            if (primaryPhoneString) {
              targetPhoneString = primaryPhoneString;
            } else {
               targetPhoneString = item.customerphonenumberSet[0];
            }

            // If we have a string
            if (targetPhoneString) {
              const parsedPhone = safeJSONParse(targetPhoneString); // Parse the selected string
               if(parsedPhone) {
                  newItem.phoneNumbers_value = parsedPhone.value || '';
               }
            }
          }

          // --- Process Payment Terms ---
          if (item.paymentTerms) {
            // Payment terms might be a stringified object or a direct object
            let paymentTermsData = typeof item.paymentTerms === 'string'
              ? safeJSONParse(item.paymentTerms)
              : item.paymentTerms;
            newItem.payment_terms_value = paymentTermsData?.value || '';
          }

          // --- Process Customer Class ---
          if (item.customerClass) {
            // Customer class might be a stringified object or a direct object
            let customerClassData = typeof item.customerClass === 'string'
              ? safeJSONParse(item.customerClass)
              : item.customerClass;
            newItem.customer_class_value = customerClassData?.value || '';
          }

          // --- Clean up original complex fields ---
          delete newItem.customeraddressSet;
          delete newItem.customeremailSet;
          delete newItem.customerphonenumberSet;
          delete newItem.paymentTerms;
          delete newItem.customerClass;
          // Also remove fields we explicitly processed to avoid duplicates/confusion
          delete newItem.firstName;
          delete newItem.lastName;
          delete newItem.outOfBusiness;
          delete newItem.adminNotes;

          // REMOVED: processConfigFields(newItem, item, config); - Explicit processing is sufficient

          return newItem;
        } catch (error) {
          console.error('Error processing Customer:', error);
          // Return a basic record with at least some data
          return {
            id: item.id || '',
            company: item.company || '',
            first_name: item.firstName || '',
            last_name: item.lastName || '',
            admin_notes: item.adminNotes || ''
          };
        }
      });
    },

    /**
     * Process Creators data for CSV export
     */
    Creators: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item };

          // Process basic fields
          newItem.display_name = item.displayName || '';
          newItem.payment_name = item.paymentName || '';
          newItem.residence = item.residence || '';
          newItem.website = item.website || '';
          newItem.biography = item.biography || '';

          // Process payment type if it exists
          if (item.paymentType) {
            if (typeof item.paymentType === 'string') {
              try {
                const paymentTypeData = safeJSONParse(item.paymentType);
                newItem.payment_type_value = paymentTypeData?.value || '';
              } catch (error) {
                console.error('Error parsing payment type:', error);
              }
            } else {
              newItem.payment_type_value = item.paymentType.value || '';
            }
          }

          // Process ALL emails (not just primary)
          if (item.creatoremailSet && Array.isArray(item.creatoremailSet) && item.creatoremailSet.length > 0) {
            // Extract primary email for the primary field
            const primaryEmail = item.creatoremailSet.find(email => email.primary === true);
            if (primaryEmail) {
              newItem.emails_value = primaryEmail.value || '';
            } else if (item.creatoremailSet[0]) {
              newItem.emails_value = item.creatoremailSet[0].value || '';
            }

            // Also add all emails as semicolon-separated list
            newItem.all_emails = item.creatoremailSet
              .map(email => email.value)
              .filter(Boolean)
              .join('; ');
          }

          // Process ALL phone numbers (not just primary)
          if (item.creatorphonenumberSet && Array.isArray(item.creatorphonenumberSet) && item.creatorphonenumberSet.length > 0) {
            // Extract primary phone for the primary field
            const primaryPhone = item.creatorphonenumberSet.find(phone => phone.primary === true);
            if (primaryPhone) {
              newItem.phoneNumbers_value = primaryPhone.value || '';
            } else if (item.creatorphonenumberSet[0]) {
              newItem.phoneNumbers_value = item.creatorphonenumberSet[0].value || '';
            }

            // Also add all phone numbers as semicolon-separated list
            newItem.all_phones = item.creatorphonenumberSet
              .map(phone => phone.value)
              .filter(Boolean)
              .join('; ');
          }

          // Process ALL addresses (not just primary)
          if (item.creatoraddressSet && Array.isArray(item.creatoraddressSet) && item.creatoraddressSet.length > 0) {
            // Find primary address or use first one for main fields
            const primaryAddress = item.creatoraddressSet.find(addr => addr.primary === true) || item.creatoraddressSet[0];
            if (primaryAddress) {
              // Map address fields to expected output fields
              newItem.addresses_line1 = primaryAddress.line1 || '';
              newItem.addresses_line2 = primaryAddress.line2 || '';
              newItem.addresses_city = primaryAddress.city || '';
              newItem.addresses_stateOrRegion = primaryAddress.stateOrRegion || '';
              newItem.addresses_country = formatCountryData(primaryAddress.country) || '';
              newItem.addresses_zipcode = primaryAddress.zipcode || '';
            }

            // Also create concatenated fields with all addresses
            const allAddresses = {
              line1: [],
              line2: [],
              city: [],
              stateOrRegion: [],
              country: [],
              zipcode: []
            };

            item.creatoraddressSet.forEach(addr => {
              if (addr.line1) allAddresses.line1.push(addr.line1);
              if (addr.line2) allAddresses.line2.push(addr.line2);
              if (addr.city) allAddresses.city.push(addr.city);
              if (addr.stateOrRegion) allAddresses.stateOrRegion.push(addr.stateOrRegion);
              if (addr.country) allAddresses.country.push(addr.country);
              if (addr.zipcode) allAddresses.zipcode.push(addr.zipcode);
            });

            // Add combined address fields
            newItem.all_address_line1 = allAddresses.line1.join('; ');
            newItem.all_address_line2 = allAddresses.line2.join('; ');
            newItem.all_address_city = allAddresses.city.join('; ');
            newItem.all_address_stateOrRegion = allAddresses.stateOrRegion.join('; ');
            newItem.all_address_country = allAddresses.country.join('; ');
            newItem.all_address_zipcode = allAddresses.zipcode.join('; ');

            // Also create full addresses for each entry
            newItem.all_addresses = item.creatoraddressSet.map(addr => {
              const parts = [
                addr.line1,
                addr.line2,
                addr.city,
                addr.stateOrRegion,
                addr.zipcode,
                addr.country
              ].filter(Boolean);
              return parts.join(', ');
            }).join(' | ');
          }

          // Process remaining fields based on config
          processConfigFields(newItem, item, config);

          return newItem;
        } catch (error) {
          console.error('Error processing Creator:', error);
          // Return basic information in case of error
          return {
            id: item.id || '',
            display_name: item.displayName || '',
            payment_name: item.paymentName || '',
            biography: item.biography || '',
            website: item.website || ''
          };
        }
      });
    },

    /**
     * Process Expenses data for CSV export
     */
    Expenses: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item };

          // Add basic expense fields
          newItem.id = item.id || '';
          newItem.amount = item.amount || '';
          newItem.expense_date = item.expenseDate || '';
          newItem.admin_notes = item.adminNotes || '';

          // Process category if it exists
          if (item.category) {
            try {
              let categoryData = item.category;

              // Handle string representation if sanitized
              if (typeof categoryData === 'string') {
                categoryData = safeJSONParse(categoryData) || {};
              }

              newItem.category_value = categoryData.value || '';
            } catch (error) {
              console.error('Error processing expense category:', error);
            }
          }

          // Extract product information if available
          if (item.product) {
            try {
              let productData = item.product;

              // Handle string representation if sanitized
              if (typeof productData === 'string') {
                productData = safeJSONParse(productData) || {};
              }

              if (productData && typeof productData === 'object') {
                newItem.product_id = productData.id || '';
                newItem.product_title = productData.title || '';
                newItem.product_isbn = productData.isbn || '';
              }
            } catch (error) {
              console.error('Error processing expense product:', error);
            }
          }

          // Process remaining fields based on config
          processConfigFields(newItem, item, config);

          return newItem;
        } catch (error) {
          console.error('Error processing Expense:', error);
          return {
            id: item.id || '',
            amount: item.amount || '',
            expense_date: item.expenseDate || ''
          };
        }
      });
    },

    /**
     * Process Royalties data for CSV export
     */
    Royalties: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item };

          // Extract product information
          if (item.product) {
            let productData = typeof item.product === 'string'
              ? safeJSONParse(item.product)
              : item.product;

            if (productData && typeof productData === 'object') {
              newItem.product_id = productData.id || '';
              newItem.product_title = productData.title || '';
              newItem.product_isbn = productData.isbn || '';
            }
          }

          // Extract creator information
          if (item.creator) {
            let creatorData = typeof item.creator === 'string'
              ? safeJSONParse(item.creator)
              : item.creator;

            if (creatorData && typeof creatorData === 'object') {
              newItem.creator_display_name = creatorData.displayName || '';
              newItem.creator_payment_name = creatorData.paymentName || '';
            }
          }

          // Calculate paid and balance amounts
          const paid = parseFloat(item.paid || 0);
          const owed = parseFloat(item.owed || 0);
          newItem.paid = paid.toFixed(2);
          newItem.balance = (owed - paid).toFixed(2);

          // Set date fields
          newItem.created = item.created || '';
          newItem.modified = item.modified || '';

          // Process remaining fields based on config
          processConfigFields(newItem, item, config);

          return newItem;
        } catch (error) {
          console.error('Error processing Royalty:', error);

          // Even with error, ensure critical fields are set
          return {
            ...item,
            product_id: item.product?.id || '',
            product_title: item.product?.title || '',
            product_isbn: item.product?.isbn || '',
            creator_display_name: item.creator?.displayName || '',
            creator_payment_name: item.creator?.paymentName || '',
            paid: parseFloat(item.paid || 0).toFixed(2),
            balance: (parseFloat(item.owed || 0) - parseFloat(item.paid || 0)).toFixed(2),
            created: item.created || '',
            modified: item.modified || ''
          };
        }
      });
    },

    /**
     * Process RoyaltyPayments data for CSV export
     */
    RoyaltyPayments: (items, config = {}) => {
      // Sanitize items first, as this processor expects stringified nested objects
      const sanitizedItems = items.map(item => sanitizeRecord(JSON.parse(JSON.stringify(item))));
      return sanitizedItems.map(item => {
        try {
          const newItem = { ...item };

          // Set basic fields
          newItem.id = item.id || '';
          newItem.amount = item.amount || '0.00';
          newItem.sent_date = item.sentDate || '';

          // Extract payment method
          if (item.method) {
            if (typeof item.method === 'string') {
              try {
                const methodData = safeJSONParse(item.method);
                if (methodData && methodData.value) {
                  newItem.method_value = methodData.value;
                }
              } catch (error) {
                console.error('Error parsing payment method:', error);
                newItem.method_value = '';
              }
            } else if (item.method && item.method.value) {
              newItem.method_value = item.method.value;
            }
          }

          // Extract royalty information first
          if (item.royalty) {
            let royaltyData = null;

            // Parse royalty data if it's a string
            if (typeof item.royalty === 'string') {
              try {
                royaltyData = safeJSONParse(item.royalty);
              } catch (error) {
                console.error('Error parsing royalty data:', error);
              }
            } else {
              royaltyData = item.royalty;
            }

            // If we have royalty data, extract product and creator information
            if (royaltyData) {
              // Extract product information from royalty
              if (royaltyData.product) {
                let productData = null;

                // Parse product data if it's a string
                if (typeof royaltyData.product === 'string') {
                  try {
                    productData = safeJSONParse(royaltyData.product);
                  } catch (error) {
                    console.error('Error parsing product data:', error);
                  }
                } else {
                  productData = royaltyData.product;
                }

                // Set product fields if product data exists
                if (productData) {
                  newItem.product_id = productData.id || '';
                  newItem.product_title = productData.title || '';
                  newItem.product_isbn = productData.isbn || '';
                }
              }

              // Extract creator information from royalty
              if (royaltyData.creator) {
                let creatorData = null;

                // Parse creator data if it's a string
                if (typeof royaltyData.creator === 'string') {
                  try {
                    creatorData = safeJSONParse(royaltyData.creator);
                  } catch (error) {
                    console.error('Error parsing creator data:', error);
                  }
                } else {
                  creatorData = royaltyData.creator;
                }

                // Set creator fields if creator data exists
                if (creatorData) {
                  newItem.creator_display_name = creatorData.displayName || '';
                  newItem.creator_payment_name = creatorData.paymentName || '';
                }
              }
            }
          }

          // Process remaining fields based on config
          processConfigFields(newItem, item, config);

          return newItem;
        } catch (error) {
          console.error('Error processing RoyaltyPayment:', error);
          // Return basic information in case of error
          return {
            id: item.id || '',
            amount: item.amount || '0.00',
            sent_date: item.sentDate || ''
          };
        }
      });
    }
  };

  /**
   * Process config fields for an item
   * This handles the generic field processing logic that's common across data types
   */
  const processConfigFields = (newItem, item, config) => {
    if (!config || !config.include) return;

    for (let k in item) {
      if (!item.hasOwnProperty(k)) continue;

      // Skip if the field is not included in the config
      if (!item[k] || (config.include.indexOf(k) === -1 &&
          !(k === 'paid' || k === 'balance' ||
            k.startsWith('product_') || k.startsWith('creator_')))) {
        continue;
      }

      try {
        // Process set fields (arrays of objects with specific field extraction)
        if (config.sets && k in config.sets) {
          processSetField(newItem, item, k, config.sets[k]);
        }
        // Process primary sets (select the "primary" item from an array)
        else if (config.primarySets && k in config.primarySets && item[k]) {
          processPrimarySetField(newItem, item, k, config.primarySets[k]);
        }
        // Process array fields
        else if (config.arrays && k in config.arrays) {
          processArrayField(newItem, item, k);
        }
        // Special handling for royalty-specific fields
        else if (k === 'paid' || k === 'balance' ||
                k.startsWith('product_') || k.startsWith('creator_')) {
          newItem[k] = item[k] || (k === 'paid' || k === 'balance' ? "0.00" : "");
        }
        // Process other config-defined fields
        else if (config[k]) {
          processConfigDefinedField(newItem, item, k, config[k]);
        }
        // Default processing for other fields
        else {
          let category = k.replace(/([A-Z])/g, "_$1").toLowerCase();
          newItem[category] = item[k];
        }
      } catch (fieldError) {
        console.error(`Error processing field ${k}:`, fieldError);
        if (k === 'paid' || k === 'balance') {
          newItem[k] = "0.00";
        } else {
          newItem[k] = '';
        }
      }
    }
  };

  /**
   * Process a set field (array of objects with field extraction)
   */
  const processSetField = (newItem, item, key, setConfig) => {
    let values = [];

    if (Array.isArray(item[key])) {
      values = item[key].map(setItem => {
        try {
          if (setItem) {
            let adjusted = makeAdjustedString(setItem);
            let parsedSet = JSON5.parse(adjusted);
            return extractSetItemValues(parsedSet, setConfig.fields, setConfig.subfields);
          }
          return "";
        } catch (parseError) {
          console.error(`Error parsing set item in ${key}:`, parseError);
          return "";
        }
      });
    } else {
      try {
        let adjusted = makeAdjustedString(item[key]);
        let parsedSet = JSON5.parse(adjusted);
        values.push(extractSetItemValues(parsedSet, setConfig.fields));
      } catch (parseError) {
        console.error(`Error parsing single item in ${key}:`, parseError);
      }
    }

    newItem[setConfig.label] = values.filter(Boolean).join("; ");
  };

  /**
   * Process a primary set field (select an object with primary=true from an array)
   */
  const processPrimarySetField = (newItem, item, key, setConfig) => {
    // Handle non-array case
    if (!Array.isArray(item[key])) {
      if (typeof item[key] === 'object' && item[key] !== null) {
        setConfig.fields.forEach(field => {
          if (item[key].hasOwnProperty(field)) {
            newItem[setConfig.label + "_" + field] = item[key][field];
          }
        });
      }
      return;
    }

    // Process array to find primary item
    let primaryArray = [];
    try {
      item[key].forEach(setItem => {
        let adjusted = makeAdjustedString(setItem);
        let parsedSet = JSON5.parse(adjusted);
        if (parsedSet.hasOwnProperty("primary") && parsedSet["primary"] === true) {
          primaryArray.push(parsedSet);
        }
      });

      // Use primary or first item
      let primarySet = primaryArray.length
        ? primaryArray[0]
        : JSON5.parse(makeAdjustedString(item[key][0]));

      // Extract fields
      setConfig.fields.forEach(field => {
        if (primarySet.hasOwnProperty(field)) {
          newItem[setConfig.label + "_" + field] = primarySet[field];
        }
      });
    } catch (error) {
      console.error(`Error processing primary set for ${key}:`, error);
    }
  };

  /**
   * Process an array field (simple arrays)
   */
  const processArrayField = (newItem, item, key) => {
    try {
      let inputString = item[key];
      if (inputString.startsWith('"') && inputString.endsWith('"')) {
        inputString = inputString.slice(1, -1);
      }

      // Remove backslashes
      inputString = inputString.replace(/\\/g, '');

      // Parse and join
      let itemArray = JSON.parse(inputString)
        .map(subItem => subItem.trim())
        .join(";");

      let category = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      newItem[category] = itemArray;
    } catch (error) {
      console.error(`Error processing array field ${key}:`, error);
      newItem[key.replace(/([A-Z])/g, "_$1").toLowerCase()] = '';
    }
  };

  /**
   * Process a config-defined field
   */
  const processConfigDefinedField = (newItem, item, key, fieldConfig) => {
    try {
      let adjusted = makeAdjustedString(item[key]);
      let parsedData = JSON5.parse(adjusted);
      let category = key.replace(/([A-Z])/g, "_$1").toLowerCase();

      if (Array.isArray(fieldConfig)) {
        fieldConfig.forEach(subItem => {
          if (parsedData && parsedData[subItem] !== undefined) {
            newItem[category + "_" + subItem] = parsedData[subItem];
          } else {
            newItem[category + "_" + subItem] = '';
          }
        });
      }
    } catch (error) {
      console.error(`Error processing config field ${key}:`, error);
    }
  };

  /**
   * Process data for CSV export
   * This is the main entry point for processing data
   */
  const processData = (data, type) => {
    try {
      if (!data) {
        return [];
      }

      // Flatten any nested arrays
      const flattenedData = Array.isArray(data) ? data : Object.values(data).flat();

      // Use the appropriate processor for this data type
      if (dataProcessors[type]) {
        // --- DEBUG LOG ---
        // console.log(`[processData] Calling processor for type: ${type}`);
        // --- END DEBUG LOG ---
        return dataProcessors[type](flattenedData, props.config);
      }

      // Fallback to generic processing if no specific processor exists
      // If no specific processor, we might still want to sanitize?
      // For now, return raw data. Consider sanitizing here if needed for generic cases.
      return flattenedData;
    } catch (error) {
      console.error('Error processing data:', error);
      toaster.danger('Error processing CSV data');
      return [];
    }
  };

  /**
   * Filter out excluded records
   */
  const filterExcludedRecords = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(item => !props.excluded.find(({ id }) => item.id === id));
  };

  // =============== DATA TYPE DEFINITIONS ===============

  // Define queries and headers for each data type
  const dataTypeDefinitions = [
    {
      name: 'Products',
      query: QUERY_PRODUCTS,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'isbn', key: 'isbn' },
        { label: 'title', key: 'title' },
        { label: 'subtitle', key: 'subtitle' },
        { label: 'format', key: 'format_description' },
        { label: 'creators', key: 'creators' },
        { label: 'description', key: 'description' },
        { label: 'publication_date', key: 'publication_date' },
        { label: 'status', key: 'status_description' },
        { label: 'retail_price', key: 'retail_price' },
        { label: 'weight', key: 'weight_value' },
        { label: 'width', key: 'width_value' },
        { label: 'height', key: 'height_value' },
        { label: 'thickness', key: 'thickness_value' },
        { label: 'page_count', key: 'page_count' },
        { label: 'edition', key: 'edition' },
        { label: 'volume', key: 'volume' },
        { label: 'series', key: 'series' },
        { label: 'imprint', key: 'imprint_value' },
        { label: 'publisher', key: 'publisher_name' },
        { label: 'ship_date', key: 'ship_date' },
        { label: 'carton_qty', key: 'carton_qty' },
        { label: 'inventory_qty', key: 'inventory_quantity' },
        { label: 'bisac', key: 'bisac' },
        { label: 'audience', key: 'audience_description' },
        { label: 'audience_details', key: 'audience_details' },
        { label: 'admin_notes', key: 'admin_notes' },
      ]
    },
    {
      name: 'Orders',
      query: QUERY_ORDERS,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'customer', key: 'company' },
        { label: 'address_line1', key: 'order_address_line1' },
        { label: 'address_line2', key: 'order_address_line2' },
        { label: 'address_city', key: 'order_address_city' },
        { label: 'address_state', key: 'order_address_stateOrRegion' },
        { label: 'address_zipcode', key: 'order_address_zipcode' },
        { label: 'email', key:'order_email_value' },
        { label: 'phone', key:'order_phone_value' },
        { label: 'po_number', key:'po_number' },
        { label: 'salesperson', key:'salesperson_value' },
        { label: 'payment_term', key:'payment_term_value' },
        { label: 'shipping_type', key:'shipping_type_value' },
        { label: 'shipping_cost', key:'shipping_cost' },
        { label: 'total_amount', key:'total_amount' },
        { label: 'paid', key:'paid' },
        { label: 'paid_date', key:'paid_date' },
        { label: 'shipped', key:'shipped' },
        { label: 'ship_date', key:'ship_date' },
        { label: 'admin_notes', key:'admin_notes' },
      ]
    },
    {
      name: 'Customers',
      query: QUERY_CUSTOMERS,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'first_name', key: 'first_name' },
        { label: 'last_name', key: 'last_name' },
        { label: 'company', key: 'company' },
        { label: 'address_line1', key: 'addresses_line1' },
        { label: 'address_line2', key: 'addresses_line2' },
        { label: 'city', key: 'addresses_city' },
        { label: 'state', key: 'addresses_stateOrRegion' },
        { label: 'zipcode', key: 'addresses_zipcode' },
        { label: 'country', key: 'addresses_country' },
        { label: 'email', key: 'emails_value' },
        { label: 'phone_number', key: 'phoneNumbers_value' },
        { label: 'payment_term', key: 'payment_terms_value' },
        { label: 'customer_class', key: 'customer_class_value' },
        { label: 'out_of_business', key: 'out_of_business' },
        { label: 'admin_notes', key: 'admin_notes' },
      ]
    },
    {
      name: 'Creators',
      query: QUERY_CREATORS,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'display_name', key: 'display_name' },
        { label: 'payment_name', key: 'payment_name' },
        { label: 'residence', key: 'residence' },
        { label: 'payment_type', key: 'payment_type_value' },
        { label: 'biography', key: 'biography' },
        { label: 'website', key: 'website' },

        // Primary email & phone
        { label: 'primary_email', key: 'emails_value' },
        { label: 'primary_phone', key: 'phoneNumbers_value' },

        // Primary address
        { label: 'primary_address_line1', key: 'addresses_line1' },
        { label: 'primary_address_line2', key: 'addresses_line2' },
        { label: 'primary_city', key: 'addresses_city' },
        { label: 'primary_state', key: 'addresses_stateOrRegion' },
        { label: 'primary_country', key: 'addresses_country' },
        { label: 'primary_zipcode', key: 'addresses_zipcode' },

        // All emails & phones (semicolon separated)
        { label: 'all_emails', key: 'all_emails' },
        { label: 'all_phones', key: 'all_phones' },

        // All addresses (semicolon separated by field)
        { label: 'all_address_line1', key: 'all_address_line1' },
        { label: 'all_address_line2', key: 'all_address_line2' },
        { label: 'all_cities', key: 'all_address_city' },
        { label: 'all_states', key: 'all_address_stateOrRegion' },
        { label: 'all_countries', key: 'all_address_country' },
        { label: 'all_zipcodes', key: 'all_address_zipcode' },

        // Complete concatenated addresses
        { label: 'all_addresses', key: 'all_addresses' },
      ]
    },
    {
      name: 'Expenses',
      query: QUERY_EXPENSES,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'expense_date', key: 'expense_date' },
        { label: 'amount', key: 'amount' },
        { label: 'category', key: 'category_value'},
        { label: 'product_id', key: 'product_id' },
        { label: 'product_title', key: 'product_title' },
        { label: 'product_isbn', key: 'product_isbn' },
        { label: 'admin_notes', key: 'admin_notes' },
      ]
    },
    {
      name: 'Royalties',
      query: QUERY_ROYALTYS,
      headers: [
        { label: 'ID', key: 'id' },
        { label: 'Product ID', key: 'product_id' },
        { label: 'Product Title', key: 'product_title' },
        { label: 'Product ISBN', key: 'product_isbn' },
        { label: 'Creator Display Name', key: 'creator_display_name' },
        { label: 'Creator Payment Name', key: 'creator_payment_name' },
        { label: 'Paid', key: 'paid' },
        { label: 'Balance', key: 'balance' },
        { label: 'Created', key: 'created' },
        { label: 'Modified', key: 'modified' }
      ]
    },
    {
      name: 'RoyaltyPayments',
      query: QUERY_ROYALTY_PAYMENTS,
      headers: [
        { label: 'id', key: 'id' },
        { label: 'payment_date', key: 'sent_date' },
        { label: 'payment_method', key: 'method_value' },
        { label: 'amount', key: 'amount' },
        { label: 'product_id', key: 'product_id' },
        { label: 'product_title', key: 'product_title' },
        { label: 'product_isbn', key: 'product_isbn' },
        { label: 'creator_display_name', key: 'creator_display_name' },
        { label: 'creator_payment_name', key: 'creator_payment_name' },
      ]
    }
  ];

  // Get the definition for the current data type
  const currentTypeDefinition = dataTypeDefinitions.find(def => def.name === props.csvType) || {};

  // Query for data if in "all mode"
  const { data } = useQuery(currentTypeDefinition.query, {
    variables: { publisher: String(currentpublisher.id), page: 1 },
    skip: !props.allmode,
  });

  // Prepare data for export - either from query or provided data
  const prepareDataForExport = () => {
    try {
      // Use data from query (allmode) or from props
      const sourceData = props.allmode ? data : props.csvData;

      if (!sourceData) {
        // Don't show warning on initial load, only when user tries to export
        return [];
      }

      // Check if data is actually available
      if ((Array.isArray(sourceData) && sourceData.length === 0) ||
          (typeof sourceData === 'object' && Object.keys(sourceData).length === 0)) {
        return [];
      }

      // Process the data based on its type
      const processedData = processData(sourceData, props.csvType);

      // Filter out excluded records
      const filteredData = filterExcludedRecords(processedData);

      // We've moved warnings to the click handlers for better control of timing
      // No warnings shown directly from here anymore

      return filteredData;
    } catch (error) {
      console.error('Error preparing data for export:', error);
      // Only show error if user has explicitly tried to export
      if (exportAttempted && !isExporting) {
        toaster.danger('Error preparing data for export', { duration: 3 });
      }
      return [];
    }
  };

  // =============== EVENT HANDLERS ===============

  /**
   * Handle clicking the CSVLink component in "all mode"
   */
  const handleAllModeClick = () => {
    try {
      // Prevent double-clicking
      if (isExporting) {
        return false;
      }

      setIsExporting(true);
      setExportAttempted(true);

      const processedData = prepareDataForExport();

      // Only show warning if we have data but nothing passes the filter
      if ((!processedData || processedData.length === 0) &&
          data &&
          data[props.csvType.toLowerCase()] &&
          data[props.csvType.toLowerCase()].length > 0) {
        toaster.warning('No records to export after filtering', { duration: 3 });
        setIsExporting(false);
        return false;
      }

      if (processedData && processedData.length > 0) {
        setFetched(processedData);
        setIsExporting(false);
        return true;
      }

      // Don't show any warnings for normal data loading
      setIsExporting(false);
      return false;
    } catch (error) {
      console.error('Error processing data for export:', error);
      toaster.danger('Error preparing data for export', { duration: 3 });
      setIsExporting(false);
      return false;
    }
  };

  /**
   * Handle clicking the CSVLink component in standard mode
   */
  const handleStandardModeClick = () => {
    try {
      // Prevent double-clicking
      if (isExporting) {
        return false;
      }

      setIsExporting(true);
      setExportAttempted(true);

      // The csv link needs data right away
      const processedData = prepareDataForExport();

      // Only show a warning if we have source data but nothing to export after filtering
      if ((!processedData || processedData.length === 0) &&
          props.csvData &&
          Array.isArray(props.csvData) &&
          props.csvData.length > 0) {
        toaster.warning('No records to export after filtering', { duration: 3 });
        setIsExporting(false);
        return false;
      }

      // Don't show any warnings for normal data loading cases
      setIsExporting(false);
      return processedData && processedData.length > 0;
    } catch (error) {
      console.error('Error preparing data for standard export:', error);
      toaster.danger('Error preparing data for export', { duration: 3 });
      setIsExporting(false);
      return false;
    }
  };

  // =============== RENDER COMPONENT ===============
  return (
    <Paragraph size={300}>
      {props.allmode ? (
        <CSVLink
          data={fetched.length > 0 ? fetched : []}
          filename={filename}
          headers={currentTypeDefinition.headers || []}
          onClick={handleAllModeClick}
          enclosingCharacter={'"'}
          onError={(error) => {
            console.error('CSV generation error:', error);
            toaster.danger('Error generating CSV file', { duration: 3 });
          }}
        >
          (Export Selected)
        </CSVLink>
      ) : (
        <CSVLink
          data={prepareDataForExport()}
          filename={filename}
          headers={currentTypeDefinition.headers || []}
          onClick={handleStandardModeClick}
          enclosingCharacter={'"'}
          onError={(error) => {
            console.error('CSV generation error:', error);
            toaster.danger('Error generating CSV file', { duration: 3 });
          }}
        >
          (Export Selected)
        </CSVLink>
      )}
    </Paragraph>
  );
}

export default CsvDownload;
